
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { RefreshCw, Info } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Skeleton } from "@/components/ui/skeleton"

interface LetterContentProps {
  letter: string
  isLoading: boolean
  onLetterChange: (letter: string) => void
  onRegenerateClick: () => void
  isEditing: boolean
  onEditingChange: (isEditing: boolean) => void
  onSubmit: () => void
  isSubmitting: boolean
  emailSending: boolean
  errorMessage: string | null
  errorDetails: string | null
  letterSource: string | null
  retryCount: number
  onRetryClick: () => void
  emailError: string | null
  emailResponse: any
  emailDebugInfo: any
}

export function LetterContent({
  letter,
  isLoading,
  onLetterChange,
  onRegenerateClick,
  isEditing,
  onEditingChange,
  onSubmit,
  isSubmitting,
  emailSending,
  errorMessage,
  errorDetails,
  letterSource,
  retryCount,
  onRetryClick,
  emailError,
  emailResponse,
  emailDebugInfo
}: LetterContentProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-center items-center mb-2">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        </div>
        <p className="text-center text-gray-600 mb-4">Crafting your letter...</p>
        <p className="text-center text-sm text-gray-500 mb-2">This may take up to 30 seconds</p>
        
        <div className="space-y-2">
          <Skeleton className="w-full h-6" />
          <Skeleton className="w-full h-6" />
          <Skeleton className="w-3/4 h-6" />
          <Skeleton className="w-full h-6" />
          <Skeleton className="w-5/6 h-6" />
          <Skeleton className="w-full h-6" />
          <Skeleton className="w-4/5 h-6" />
        </div>
      </div>
    )
  }
  
  return (
    <>
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle className="flex items-center gap-2">
            <Info className="h-4 w-4" />
            Letter Generation Issue
          </AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <p>{errorMessage}</p>
            {errorDetails && (
              <details className="text-xs mt-1">
                <summary className="cursor-pointer">Technical Details</summary>
                <p className="mt-1 whitespace-pre-wrap">{errorDetails}</p>
              </details>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRetryClick}
              className="self-start mt-2"
            >
              Try Again ({retryCount})
            </Button>
          </AlertDescription>
        </Alert>
      )}
      
      {emailError && (
        <Alert variant="warning" className="mb-4">
          <AlertTitle className="flex items-center gap-2">
            <Info className="h-4 w-4" />
            Email Notification
          </AlertTitle>
          <AlertDescription>
            <p>There may be issues with email notifications. Your letter will still be processed.</p>
            <details className="text-xs mt-1">
              <summary className="cursor-pointer">Email Debug Details</summary>
              <pre className="mt-1 whitespace-pre-wrap overflow-x-auto text-xs">
                {JSON.stringify({
                  error: emailError,
                  response: emailResponse,
                  debugInfo: emailDebugInfo
                }, null, 2)}
              </pre>
            </details>
          </AlertDescription>
        </Alert>
      )}
      
      {letterSource && letterSource !== 'gemini' && !errorMessage && (
        <Alert variant="default" className="mb-4">
          <AlertTitle className="flex items-center gap-2">
            <Info className="h-4 w-4" />
            Using Template Letter
          </AlertTitle>
          <AlertDescription>
            We're using a template letter. Feel free to edit it to meet your needs or try regenerating.
          </AlertDescription>
        </Alert>
      )}
      
      <Textarea
        value={letter}
        onChange={(e) => onLetterChange(e.target.value)}
        className="min-h-[400px] font-mono text-sm"
        placeholder="Your letter will appear here. If it doesn't load, you can write your own or click regenerate."
      />
      
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <Button 
            variant="outline"
            onClick={onRegenerateClick}
            className="bg-[#29b554] hover:bg-[#29b554]/90 text-white"
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Regenerate
          </Button>
        </div>
        
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={() => onEditingChange(!isEditing)}
          >
            {isEditing ? "Done Editing" : "Edit Letter"}
          </Button>
          <Button 
            onClick={onSubmit}
            disabled={isSubmitting || !letter.trim() || isLoading || emailSending}
          >
            {isSubmitting || emailSending ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                {isSubmitting ? "Submitting..." : "Sending Emails..."}
              </>
            ) : (
              "Submit Letter"
            )}
          </Button>
        </div>
      </div>
    </>
  )
}
